import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, 
  Camera, 
  Video, 
  PartyPopper, 
  Users, 
  ArrowRight, 
  Sparkles,
  Check,
  Heart
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import confetti from "canvas-confetti";

interface Package {
  id: string;
  name: string;
  price: number;
  category: string | null;
  features: string[];
}

const budgetOptions = [
  { label: "Under $1,500", value: "under-1500", max: 1500 },
  { label: "$1,500 - $2,500", value: "1500-2500", min: 1500, max: 2500 },
  { label: "$2,500 - $4,000", value: "2500-4000", min: 2500, max: 4000 },
  { label: "$4,000+", value: "4000-plus", min: 4000 },
];

const serviceOptions = [
  { label: "Photography", value: "photo", icon: Camera },
  { label: "Videography", value: "video", icon: Video },
  { label: "Photo Booth", value: "photobooth", icon: PartyPopper },
];

const guestOptions = [
  { label: "Under 50", value: "small", description: "Intimate gathering" },
  { label: "50 - 150", value: "medium", description: "Classic wedding" },
  { label: "150 - 300", value: "large", description: "Grand celebration" },
  { label: "300+", value: "xlarge", description: "Mega event" },
];

export function PackageQuiz() {
  const [step, setStep] = useState(0);
  const [budget, setBudget] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [guests, setGuests] = useState("");
  const [packages, setPackages] = useState<Package[]>([]);
  const [recommendation, setRecommendation] = useState<Package | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPackages = async () => {
      const { data } = await supabase
        .from("pricing_packages")
        .select("*")
        .eq("is_active", true)
        .order("price", { ascending: true });
      
      if (data) setPackages(data);
    };
    fetchPackages();
  }, []);

  const toggleService = (value: string) => {
    setServices(prev => 
      prev.includes(value) 
        ? prev.filter(s => s !== value)
        : [...prev, value]
    );
  };

  const findRecommendation = () => {
    const budgetOption = budgetOptions.find(b => b.value === budget);
    const hasPhoto = services.includes("photo");
    const hasVideo = services.includes("video");
    
    let filtered = packages;
    
    // Filter by budget
    if (budgetOption) {
      filtered = filtered.filter(p => {
        if (budgetOption.max && !budgetOption.min) return p.price <= budgetOption.max;
        if (budgetOption.min && !budgetOption.max) return p.price >= budgetOption.min;
        if (budgetOption.min && budgetOption.max) return p.price >= budgetOption.min && p.price <= budgetOption.max;
        return true;
      });
    }
    
    // Try to match category based on services
    if (hasPhoto && hasVideo) {
      const bundles = filtered.filter(p => p.category === "bundle" || p.name.toLowerCase().includes("bundle") || p.name.toLowerCase().includes("combo"));
      if (bundles.length > 0) filtered = bundles;
    } else if (hasPhoto && !hasVideo) {
      const photoOnly = filtered.filter(p => p.category === "photography" || p.name.toLowerCase().includes("photo"));
      if (photoOnly.length > 0) filtered = photoOnly;
    } else if (hasVideo && !hasPhoto) {
      const videoOnly = filtered.filter(p => p.category === "videography" || p.name.toLowerCase().includes("video") || p.name.toLowerCase().includes("cinema"));
      if (videoOnly.length > 0) filtered = videoOnly;
    }
    
    // Get the best match (middle of filtered range for best value)
    const recommended = filtered.length > 0 
      ? filtered[Math.floor(filtered.length / 2)] 
      : packages[Math.floor(packages.length / 2)];
    
    setRecommendation(recommended);
    setStep(4);
    
    // Trigger confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#f472b6', '#ec4899', '#db2777', '#be185d']
    });
  };

  const handleGetStarted = () => {
    if (!recommendation) return;
    
    // Build comprehensive URL params
    const params = new URLSearchParams();
    params.set("package", recommendation.name);
    params.set("quiz_budget", budget);
    params.set("quiz_services", services.join(","));
    params.set("quiz_guests", guests);
    
    navigate(`/contact?${params.toString()}`);
  };

  const getBudgetLabel = () => budgetOptions.find(b => b.value === budget)?.label || "";
  const getServicesLabel = () => services.map(s => serviceOptions.find(o => o.value === s)?.label).filter(Boolean).join(", ");
  const getGuestsLabel = () => guestOptions.find(g => g.value === guests)?.label || "";

  return (
    <section className="section-padding relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
      <div className="absolute top-10 left-10 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-accent/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/4 opacity-20">
        <Heart className="w-8 h-8 text-primary animate-pulse" />
      </div>
      <div className="absolute top-1/3 right-1/4 opacity-20">
        <Sparkles className="w-6 h-6 text-primary animate-pulse" style={{ animationDelay: '0.5s' }} />
      </div>
      
      <div className="container-wide relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <Badge variant="secondary" className="mb-4 bg-primary/20 text-primary border-primary/30">
            <Sparkles className="w-3 h-3 mr-1" />
            60-Second Quiz
          </Badge>
          <h2 className="font-serif text-3xl md:text-5xl text-foreground mb-3">
            Not Sure Which Package?
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Answer 3 quick questions and we'll recommend the perfect package for your day
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <Card className="p-8 md:p-12 shadow-2xl border-2 border-primary/20 bg-card/95 backdrop-blur-sm rounded-3xl relative overflow-hidden">
            {/* Glow effect */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/30 rounded-full blur-3xl" />
            
            {/* Progress dots */}
            {step > 0 && step < 4 && (
              <div className="flex justify-center gap-2 mb-8">
                {[1, 2, 3].map((s) => (
                  <motion.div
                    key={s}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      s <= step ? "bg-primary" : "bg-muted"
                    }`}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: s === step ? 1.2 : 1 }}
                  />
                ))}
              </div>
            )}

            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="start"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center space-y-6"
                >
                  <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto">
                    <Sparkles className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-2">
                      Let's Find Your Perfect Match
                    </h3>
                    <p className="text-muted-foreground">
                      Takes less than a minute • Personalized recommendation
                    </p>
                  </div>
                  <Button 
                    size="lg" 
                    onClick={() => setStep(1)}
                    className="group text-lg px-8 py-6 rounded-full"
                  >
                    Start Quiz
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  key="budget"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <DollarSign className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-serif text-2xl text-foreground mb-2">
                      What's your budget range?
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      We have options for every budget
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {budgetOptions.map((option) => (
                      <motion.button
                        key={option.value}
                        onClick={() => setBudget(option.value)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          budget === option.value
                            ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                            : "border-border hover:border-primary/50 bg-background"
                        }`}
                      >
                        <span className="font-medium text-foreground">{option.label}</span>
                      </motion.button>
                    ))}
                  </div>
                  <div className="flex justify-between pt-4">
                    <Button variant="ghost" onClick={() => setStep(0)}>
                      Back
                    </Button>
                    <Button onClick={() => setStep(2)} disabled={!budget}>
                      Next <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="services"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Camera className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-serif text-2xl text-foreground mb-2">
                      What services do you need?
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Select all that apply
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {serviceOptions.map((option) => {
                      const Icon = option.icon;
                      const isSelected = services.includes(option.value);
                      return (
                        <motion.button
                          key={option.value}
                          onClick={() => toggleService(option.value)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`p-5 rounded-xl border-2 flex items-center gap-4 transition-all ${
                            isSelected
                              ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                              : "border-border hover:border-primary/50 bg-background"
                          }`}
                        >
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                          }`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <span className="font-medium text-foreground text-lg">{option.label}</span>
                          {isSelected && (
                            <Check className="w-5 h-5 text-primary ml-auto" />
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                  <div className="flex justify-between pt-4">
                    <Button variant="ghost" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button onClick={() => setStep(3)} disabled={services.length === 0}>
                      Next <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="guests"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-serif text-2xl text-foreground mb-2">
                      How many guests are you expecting?
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      This helps us recommend the right coverage
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {guestOptions.map((option) => (
                      <motion.button
                        key={option.value}
                        onClick={() => setGuests(option.value)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          guests === option.value
                            ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                            : "border-border hover:border-primary/50 bg-background"
                        }`}
                      >
                        <span className="block font-medium text-foreground">{option.label}</span>
                        <span className="text-sm text-muted-foreground">{option.description}</span>
                      </motion.button>
                    ))}
                  </div>
                  <div className="flex justify-between pt-4">
                    <Button variant="ghost" onClick={() => setStep(2)}>
                      Back
                    </Button>
                    <Button onClick={findRecommendation} disabled={!guests}>
                      See My Match <Sparkles className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 4 && recommendation && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center space-y-6"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    className="w-20 h-20 bg-gradient-to-br from-primary to-primary/60 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-primary/30"
                  >
                    <Check className="w-10 h-10 text-primary-foreground" />
                  </motion.div>
                  
                  <div>
                    <Badge className="mb-3 bg-primary/20 text-primary">
                      Perfect Match Found!
                    </Badge>
                    <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-2">
                      {recommendation.name}
                    </h3>
                    <p className="text-2xl font-semibold text-primary mb-4">
                      ${recommendation.price.toLocaleString()}
                    </p>
                  </div>

                  {/* Quiz summary */}
                  <div className="bg-muted/50 rounded-xl p-4 text-left space-y-2 text-sm">
                    <p className="text-muted-foreground font-medium mb-2">Based on your preferences:</p>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-primary" />
                      <span className="text-foreground">{getBudgetLabel()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Camera className="w-4 h-4 text-primary" />
                      <span className="text-foreground">{getServicesLabel()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="text-foreground">{getGuestsLabel()} guests</span>
                    </div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Button 
                      size="lg" 
                      onClick={handleGetStarted}
                      className="w-full text-lg py-6 rounded-full animate-pulse hover:animate-none"
                    >
                      Check Availability & Lock In Your Date
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <p className="text-xs text-muted-foreground mt-3">
                      Your quiz results will be saved with your inquiry
                    </p>
                  </motion.div>

                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setStep(0);
                      setBudget("");
                      setServices([]);
                      setGuests("");
                      setRecommendation(null);
                    }}
                  >
                    Retake Quiz
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}

export default PackageQuiz;
